import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  HardHat,
  Home as HomeIcon,
  PackageCheck,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const productModules = [
  {
    icon: BarChart3,
    title: 'Portfolio intelligence',
    body: 'Track cost trends, schedule variance, approval aging, and field activity across projects.',
    highlights: ['Portfolio KPIs', 'Schedule variance', 'Cost trends', 'Approval aging'],
  },
  {
    icon: HomeIcon,
    title: 'AI plans and estimates',
    body: 'Generate house plans, project estimates, BOQs, line items, reviews, and revisions from guided prompts.',
    highlights: ['AI generation', 'BOQ line items', 'Prompt templates', 'Drawing versions'],
  },
  {
    icon: HardHat,
    title: 'Daily site control',
    body: 'Capture progress, labor attendance, weather notes, site observations, and offline field updates.',
    highlights: ['Daily reports', 'Labor check-in', 'Material usage', 'Offline sync'],
  },
  {
    icon: FileText,
    title: 'Controlled documents',
    body: 'Manage drawings, specifications, contracts, reports, revisions, permissions, and download tracking.',
    highlights: ['Document register', 'Version history', 'Project files', 'Permissions'],
  },
  {
    icon: PackageCheck,
    title: 'Materials and finance',
    body: 'Coordinate catalogs, requisitions, purchase orders, GRNs, vendors, invoices, payments, and ledgers.',
    highlights: ['Purchase orders', 'Goods receipts', 'Vendor ledger', 'Invoice approval'],
  },
  {
    icon: ClipboardCheck,
    title: 'Quality handover',
    body: 'Run inspections, punch lists, evidence capture, status updates, handover packs, and certificates.',
    highlights: ['Punch lists', 'Status updates', 'Handover packs', 'Certificates'],
  },
];

const whyBuildOrq = [
  {
    title: 'Construction work is too connected for scattered tools.',
    body: 'A drawing change can affect estimates, procurement, site execution, inspection status, and payment approvals. BuildOrq keeps those workflows in one shared operating layer.',
  },
  {
    title: 'Teams need faster decisions with a reliable audit trail.',
    body: 'Daily reports, approvals, documents, vendor invoices, and handover evidence stay organized so managers can see what changed, who acted, and what needs attention next.',
  },
  {
    title: 'AI is useful only when it lands inside the workflow.',
    body: 'BuildOrq brings AI-assisted estimates and house plans into the same place where teams review, revise, approve, procure, inspect, and report.',
  },
];

const Home = () => (
  <>
    <SEOHead
      absoluteTitle="BuildOrq | Orchestrate the Build"
      description="BuildOrq is a construction operations platform for AI-assisted estimates, house plans, field reports, documents, materials, finance, inspections, and analytics."
    />

    {/* Hero */}
    <section className="bg-hero-fade py-24 lg:py-32">
      <div className="container-custom text-center">
        <motion.div {...fadeUp} transition={{ duration: 0.4 }}>
          <p className="eyebrow">Construction operations platform</p>
          <h1 className="section-title mx-auto mt-3 max-w-3xl">
            Orchestrate estimates, plans, site work, and project controls in one place.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-surface-body">
            BuildOrq gives construction teams one workspace for AI-assisted
            project estimates, generated house plans, daily site activity,
            documents, materials, inspections, finance, and portfolio reporting.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="https://app.buildorq.com/signup"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Sign up free
            </a>
            <a href="#how" className="btn-secondary">
              See how it works
              <ArrowRight size={16} className="ml-2" aria-hidden />
            </a>
          </div>
        </motion.div>
      </div>
    </section>

    {/* How it works */}
    <section id="how" className="bg-white py-20">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">How it works</p>
          <h2 className="section-title mt-2">From project setup to handover.</h2>
        </div>
        <div className="mx-auto mt-14 grid max-w-4xl gap-8 md:grid-cols-3">
          {[
            { step: '01', title: 'Create the project workspace', body: 'Start with projects, teams, document registers, and the records that every downstream workflow depends on.' },
            { step: '02', title: 'Generate plans and estimates', body: 'Use guided prompts and templates to produce house plans, cost estimates, BOQs, and review-ready revisions.' },
            { step: '03', title: 'Run the build rhythm', body: 'Coordinate site reports, materials, invoices, inspections, approvals, notifications, and portfolio reporting.' },
          ].map(({ step, title, body }) => (
            <motion.div
              key={step}
              {...fadeUp}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card"
            >
              <span className="text-3xl font-bold text-primary-200">{step}</span>
              <h3 className="mt-3 text-lg font-semibold text-surface-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Key features */}
    <section className="bg-surface-page py-20">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Product modules</p>
          <h2 className="section-title mt-2">Built around real construction workflows.</h2>
        </div>
        <div className="mx-auto mt-14 grid max-w-6xl gap-6 md:grid-cols-2 lg:grid-cols-3">
          {productModules.map(({ icon: Icon, title, body, highlights }) => (
            <div
              key={title}
              className="rounded-2xl border border-slate-200 bg-surface-card p-6 shadow-card"
            >
              <Icon size={28} className="text-primary-600" aria-hidden />
              <h3 className="mt-4 text-base font-semibold text-surface-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Highlighted features
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {highlights.map(feature => (
                    <span
                      key={feature}
                      className="rounded-full border border-primary-100 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Why BuildOrq */}
    <section className="bg-white py-20">
      <div className="container-custom">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <p className="eyebrow">Why BuildOrq</p>
            <h2 className="section-title mt-2">
              Construction teams need one source of operational truth.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-surface-body">
              Projects move through estimates, drawings, procurement, daily site
              execution, invoices, inspections, and handover. When those pieces
              live in separate chats, spreadsheets, drives, and point tools,
              delays compound and leaders lose confidence in the latest status.
            </p>
          </div>

          <div className="space-y-4">
            {whyBuildOrq.map(({ title, body }) => (
              <div
                key={title}
                className="flex gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6"
              >
                <CheckCircle2
                  size={22}
                  className="mt-0.5 shrink-0 text-primary-600"
                  aria-hidden
                />
                <div>
                  <h3 className="text-base font-semibold text-surface-ink">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Workflow coverage */}
    <section className="bg-white py-16">
      <div className="container-custom">
        <p className="text-center text-sm font-semibold uppercase tracking-wider text-slate-400">
          Coverage inside the BuildOrq app
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-10 opacity-40">
          {[
            'Analytics',
            'Daily Site',
            'Documents',
            'Estimations',
            'Finance',
            'House Plans',
            'Inspections',
            'Materials',
            'Notifications',
          ].map(label => (
            <div
              key={label}
              className="flex h-9 min-w-28 items-center justify-center rounded border border-slate-200 bg-slate-50 px-4 text-xs font-semibold text-slate-500"
            >
              {label}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="bg-primary-600 py-20">
      <div className="container-custom text-center">
        <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
          Bring your construction workflows into one operating system.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-primary-100">
          Start with estimates, plans, documents, and site activity, then connect
          materials, finance, quality, notifications, and analytics as the team grows.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a
            href="https://app.buildorq.com/signup"
            rel="noopener noreferrer"
            className="rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-700 shadow-soft transition hover:bg-primary-50"
          >
            Sign up free
          </a>
          <a
            href="/demo"
            className="rounded-lg border border-primary-400 px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-700"
          >
            Try demo
          </a>
        </div>
      </div>
    </section>
  </>
);

export default Home;
